/* eslint-disable react-hooks/exhaustive-deps */

import React, { useLayoutEffect, useState } from "react";

import {
  Space,
  Button,
  Card,
  Popconfirm,
  Layout,
  Table,
  Input,
  Select,
  Switch,
} from "antd";

import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";

import { useNavigate } from "react-router-dom";

import LayoutHeader from "../inc/LayoutHeader";
import LayoutFooter from "../inc/LayoutFooter";

import { displayMessage, getAPI, postAPI } from "../../utils/common";

import { ERROR_MSG_TYPE, SUCCESS_MESSAGE } from "../../constants/messages";

import {
  SMART_PRICE_PRICING_RULES,
  SMART_PRICE_DELETE_PRICING_RULE,
} from "../../constants/api";

import PricingRuleModal from "./PricingRuleModal";
import LS_SERVICE from "../../utils/localStorage";

function SmartPricePricingRules() {
  const { Content } = Layout;

  const navigate = useNavigate();

  const [state, setState] = useState([]);

  const [filteredState, setFilteredState] = useState([]);

  const [loading, setLoading] = useState(true);

  const [searchText, setSearchText] = useState("");

  const [typeFilter, setTypeFilter] = useState(null);

  const [statusFilter, setStatusFilter] = useState(null);

  const [isModalOpen, setIsModalOpen] = useState(false);

  const [editData, setEditData] = useState(null);

  const userType = LS_SERVICE.get("user_type");
  const isSuperAdmin = userType === "superAdmin";
  const isAdmin = userType === "admin";

  const showDelete = isSuperAdmin;
  const showEdit = isSuperAdmin || isAdmin;

  useLayoutEffect(() => {
    getList();
  }, []);

  /*
  |--------------------------------------------------------------------------
  | Get List
  |--------------------------------------------------------------------------
  */

  const getList = () => {
    setLoading(true);

    const successFn = (res) => {
      if (res.status === true) {
        setLoading(false);

        const rows = res.data.data ?? res.data;

        setState(rows);

        setFilteredState(rows);
      } else {
        setState([]);

        setFilteredState([]);

        setLoading(false);
      }
    };

    const errorFn = () => {
      setLoading(false);

      displayMessage(ERROR_MSG_TYPE, "Unable to get pricing rules");
    };

    getAPI(SMART_PRICE_PRICING_RULES, successFn, errorFn);
  };

  /*
  |--------------------------------------------------------------------------
  | Search + Filter
  |--------------------------------------------------------------------------
  */

  const handleSearch = (value) => {
    setSearchText(value);

    filterData(value, typeFilter, statusFilter);
  };

  const filterData = (search, type, status) => {
    let data = [...state];

    if (search) {
      data = data.filter(
        (item) =>
          item.title?.toLowerCase().includes(search.toLowerCase()) ||
          item.rule_key?.toLowerCase().includes(search.toLowerCase()),
      );
    }

    if (type) {
      data = data.filter((item) => item.type === type);
    }

    if (status !== null) {
      data = data.filter((item) => item.is_active == status);
    }

    setFilteredState(data);
  };

  /*
  |--------------------------------------------------------------------------
  | Delete
  |--------------------------------------------------------------------------
  */

  const deleteData = (id) => {
    setLoading(true);

    const data = new FormData();

    data.append("id", id);

    const successFn = (res) => {
      if (res.status === true) {
        displayMessage(SUCCESS_MESSAGE, res.message);

        getList();
      } else {
        setLoading(false);

        displayMessage(ERROR_MSG_TYPE, res.message);
      }
    };

    const errorFn = () => {
      setLoading(false);

      displayMessage(ERROR_MSG_TYPE, "Unable to delete rule");
    };

    postAPI(SMART_PRICE_DELETE_PRICING_RULE, data, successFn, errorFn);
  }; /*
  |--------------------------------------------------------------------------
  | Modal
  |--------------------------------------------------------------------------
  */

  const openAddModal = () => {
    setEditData(null);

    setIsModalOpen(true);
  };

  const openEditModal = (record) => {
    setEditData(record);

    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);

    setEditData(null);
  };

  const afterSave = () => {
    closeModal();

    getList();
  };

  /*
  |--------------------------------------------------------------------------
  | Table Columns
  |--------------------------------------------------------------------------
  */

  const columns = [
    {
      title: "Sno",
      dataIndex: "ID",
      width: 70,
    },

    {
      title: "Rule Key",
      dataIndex: "RuleKey",
      width: 180,
    },

    {
      title: "Title",
      dataIndex: "Title",
      width: 250,
    },

    {
      title: "Value",
      dataIndex: "Value",
      width: 100,
    },

    {
      title: "Type",
      dataIndex: "Type",
      width: 120,
    },

    {
      title: "Status",
      dataIndex: "Status",
      width: 120,
    },

    {
      title: "Action",
      dataIndex: "Action",
      width: 180,
    },
  ];

  const tableData = filteredState.map((row, index) => ({
    key: row.id,

    ID: index + 1,

    RuleKey: row.rule_key,

    Title: row.title,

    Value: row.value,

    Type: row.type,

    Status: <Switch checked={row.is_active == 1} disabled />,

    Action: (
      <Space>
        {showEdit && (
          <Button
            type="primary"
            ghost
            icon={<EditOutlined />}
            onClick={() => openEditModal(row)}
          >
            Edit
          </Button>
        )}
        {showDelete && (
          <Popconfirm
            title="Delete pricing rule"
            description="Are you sure to delete this rule?"
            okText="Yes"
            cancelText="No"
            onConfirm={() => deleteData(row.id)}
          >
            <Button danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        )}
      </Space>
    ),
  }));

  return (
    <Layout>
      <LayoutHeader />

      <Content>
        <Card
          title="Manage Pricing Rules"
          bordered
          extra={
            <Space>
              <Input
                placeholder="Search by title/key"
                value={searchText}
                onChange={(e) => handleSearch(e.target.value)}
                style={{
                  width: 250,
                }}
              />

              <Select
                placeholder="Type"
                allowClear
                style={{
                  width: 120,
                }}
                onChange={(value) => {
                  setTypeFilter(value);

                  filterData(searchText, value, statusFilter);
                }}
                options={[
                  {
                    label: "Number",
                    value: "number",
                  },

                  {
                    label: "Percentage",
                    value: "percentage",
                  },

                  {
                    label: "Score",
                    value: "score",
                  },
                ]}
              />

              <Select
                placeholder="Status"
                allowClear
                style={{
                  width: 120,
                }}
                onChange={(value) => {
                  setStatusFilter(value);

                  filterData(searchText, typeFilter, value);
                }}
                options={[
                  {
                    label: "Active",
                    value: 1,
                  },

                  {
                    label: "Inactive",
                    value: 0,
                  },
                ]}
              />

              <Button type="primary" onClick={openAddModal}>
                Add <PlusOutlined />
              </Button>
            </Space>
          }
        >
          <Table
            loading={loading}
            columns={columns}
            dataSource={tableData}
            pagination={{
              pageSize: 10,
            }}
            scroll={{
              x: 900,
            }}
          />
        </Card>
      </Content>

      <LayoutFooter />

      <PricingRuleModal
        open={isModalOpen}
        close={closeModal}
        editData={editData}
        afterSave={afterSave}
      />
    </Layout>
  );
}

export default SmartPricePricingRules;
